
import React from 'react';
import type { ProductVariant } from '@/types/product';
import { Check } from 'lucide-react';

interface ColorSelectorProps {
  variants: ProductVariant[];
  selectedVariant: ProductVariant;
  onVariantChange: (variant: ProductVariant) => void;
}

const ColorSelector: React.FC<ColorSelectorProps> = ({
  variants,
  selectedVariant,
  onVariantChange,
}) => {
  // Extraire les couleurs uniques des variantes
  const uniqueColors = variants.reduce<{name: string, hex: string}[]>((acc, variant) => {
    if (variant.colorName && variant.colorHex && !acc.some(c => c.name === variant.colorName)) {
      acc.push({
        name: variant.colorName,
        hex: variant.colorHex
      });
    }
    return acc;
  }, []);
  
  console.log("ColorSelector: Couleurs uniques trouvées", uniqueColors);

  // Fonction pour trouver une variante avec la couleur sélectionnée
  const findVariantWithColor = (colorName: string) => {
    // Si ce produit a des tailles, maintenir la taille sélectionnée si possible
    if (selectedVariant.size) {
      // Chercher une variante avec la même taille mais la nouvelle couleur
      const sameSizeNewColor = variants.find(v => 
        v.colorName === colorName && v.size === selectedVariant.size
      );
      
      if (sameSizeNewColor) {
        return sameSizeNewColor;
      }
    }
    
    // Sinon, prendre la première variante avec cette couleur
    return variants.find(v => v.colorName === colorName) || selectedVariant;
  };

  const handleColorChange = (colorName: string) => {
    const newVariant = findVariantWithColor(colorName);
    console.log("ColorSelector: Changement de couleur", {
      previousVariant: `${selectedVariant.size} - ${selectedVariant.colorName}`,
      newVariant: `${newVariant.size} - ${newVariant.colorName}`
    });
    onVariantChange(newVariant);
  };

  if (uniqueColors.length <= 1) return null;

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Couleur: <span className="font-normal">{selectedVariant.colorName}</span>
      </label>
      <div className="flex flex-wrap gap-3">
        {uniqueColors.map(color => (
          <button
            key={color.name}
            onClick={() => handleColorChange(color.name)}
            className={`
              relative w-9 h-9 rounded-full overflow-hidden
              ${selectedVariant.colorName === color.name 
                ? 'ring-2 ring-offset-2 ring-black' 
                : 'hover:ring-1 hover:ring-offset-1 hover:ring-gray-300'
              }
            `}
            aria-pressed={selectedVariant.colorName === color.name}
            aria-label={`Couleur ${color.name}`}
            title={color.name}
            style={{ backgroundColor: color.hex }}
          >
            {selectedVariant.colorName === color.name && (
              <span className="absolute inset-0 flex items-center justify-center">
                <Check className={`h-5 w-5 text-white drop-shadow-[0_0_1px_rgba(0,0,0,0.5)]`} />
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

export default ColorSelector;
